import styled from 'styled-components'

export const CanvasWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  overflow: hidden;
  margin: 10px 0 20px;
  background: ${({ theme }) => theme.colors.surfaceElevated};

  ${({ $isFullscreen }) => $isFullscreen && 'height: 100vh;'}
`

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
`

export const ToolButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.onPrimary : theme.colors.textPrimary)};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const ToolbarSeparator = styled.div`
  width: 1px;
  align-self: stretch;
  background: ${({ theme }) => theme.colors.border};
  margin: 0 4px;
`

// Zero-height, full-width flex item that forces everything after it onto a new toolbar row
// (used in fullscreen to push the results/action controls to a second line).
export const ToolbarBreak = styled.div`
  flex-basis: 100%;
  height: 0;
`

export const ToolbarSelect = styled.select`
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.inputBackground};
  color: ${({ theme }) => theme.colors.textPrimary};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const CanvasBody = styled.div`
  display: flex;
  align-items: stretch;
  ${({ $isFullscreen }) => $isFullscreen && 'flex: 1; min-height: 0;'}

  @media (max-width: 720px) {
    flex-direction: column;
  }
`

export const StageHost = styled.div`
  flex: 1;
  min-width: 0;
  cursor: ${({ $cursor }) => $cursor || 'default'};
`

export const PropertiesPanel = styled.div`
  width: 260px;
  flex-shrink: 0;
  padding: 16px;
  overflow-y: auto;
  border-left: 2px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};

  h4 {
    margin: 0 0 12px;
  }

  @media (max-width: 720px) {
    width: auto;
    border-left: none;
    border-top: 2px solid ${({ theme }) => theme.colors.border};
  }
`

export const PropertyRow = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  margin-bottom: 10px;
  min-width: 0;
  color: ${({ theme }) => theme.colors.textPrimary};

  input,
  select {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    padding: 6px 8px;
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.colors.inputBorder};
    background: ${({ theme }) => theme.colors.inputBackground};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`

export const PropertyCheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const PropertiesEmptyState = styled.p`
  font-size: 13px;
  opacity: 0.7;
  font-style: italic;
`

export const NearbyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  h5 {
    margin: 0 0 2px;
    font-size: 12px;
    font-weight: 600;
    opacity: 0.7;
    text-transform: uppercase;
  }
`

export const NearbyItemButton = styled.button`
  text-align: left;
  padding: 6px 8px;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  background: ${({ $active, theme }) => ($active ? `${theme.colors.primary}22` : 'transparent')};
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

export const HintBar = styled.div`
  padding: 8px 14px;
  font-size: 13px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(125,145,255,0.08)' : 'rgba(102,126,234,0.06)')};
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const LibraryOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.45);
`

export const LibraryModal = styled.div`
  width: 80%;
  height: 80%;
  max-width: 1400px;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
`

// A compact variant for small settings dialogs (e.g. view options) that shouldn't span the
// whole canvas like the data-heavy library modal does.
export const OptionsModal = styled(LibraryModal)`
  width: auto;
  min-width: 340px;
  max-width: 440px;
  height: auto;
  max-height: 80%;
`

export const OptionsSectionTitle = styled.h5`
  margin: 18px 0 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.7;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:first-of-type {
    margin-top: 0;
  }
`

export const LibraryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};

  button {
    background: transparent;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 18px;
    display: flex;
    align-items: center;
  }
`

export const LibraryTabs = styled.div`
  display: flex;
  gap: 6px;
`

export const LibraryTabButton = styled.button`
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.onPrimary : theme.colors.textPrimary)};
`

export const LibraryBody = styled.div`
  flex: 1;
  min-height: 0;
  padding: 16px;
  overflow-y: auto;
`

export const LibraryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`

export const LibraryTh = styled.th`
  text-align: left;
  font-size: 12px;
  padding: 6px 8px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const LibraryTd = styled.td`
  padding: 6px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  input,
  select {
    width: 100%;
    min-width: 70px;
    padding: 6px 8px;
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.colors.inputBorder};
    background: ${({ theme }) => theme.colors.inputBackground};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`

export const LibraryFootRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`

export const SmallButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.onPrimary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const ChartLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 8px 0 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .swatch {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;
  }
`

export const SummaryCardsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 12px 0 20px;
`

export const SummaryCard = styled.div`
  flex: 1;
  min-width: 150px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceElevated};

  h5 {
    margin: 0 0 6px;
    font-size: 12px;
    font-weight: 600;
    opacity: 0.7;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  strong {
    display: block;
    font-size: 16px;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  small {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`

export const MovingLoadsInfoNote = styled.p`
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  margin: 0 0 16px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(125,145,255,0.08)' : 'rgba(102,126,234,0.06)')};
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.danger};
  background: transparent;
  color: ${({ theme }) => theme.colors.danger};

  &:hover {
    background: ${({ theme }) => theme.colors.danger};
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
